import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import unitService, { Unit } from '../../services/unitService';
import unitConversionService, { UnitConversion } from '../../services/unitConversionService';
import { parseApiError } from '../../utils/errorUtils';
import '../../styles/ImsTheme.css';
import '../Products/ProductCategoryEditPage.css';
import { ArrowLeft, Save } from 'lucide-react';

// Max length constants for validation
const MAX_LENGTHS = {
  remarks: 200,
};

interface FieldErrors {
  fromUnitId?: string;
  toUnitId?: string;
  conversionFactor?: string;
  remarks?: string;
}

interface UnitConversionForm {
  fromUnitId: string;
  toUnitId: string;
  conversionFactor: string;
  remarks: string;
  isActive: boolean;
}

const UnitConversionEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [conversion, setConversion] = useState<UnitConversion | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [units, setUnits] = useState<Unit[]>([]);
  const [formData, setFormData] = useState<UnitConversionForm>({
    fromUnitId: '',
    toUnitId: '',
    conversionFactor: '',
    remarks: '',
    isActive: true,
  });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [unitsData, conversionData] = await Promise.all([
        unitService.getUnits(1, 100),
        unitConversionService.getUnitConversionById(parseInt(id || '0', 10)),
      ]);

      if (Array.isArray(unitsData)) {
        setUnits(unitsData);
      }

      if (conversionData) {
        setConversion(conversionData);
        setFormData({
          fromUnitId: String(conversionData.fromUnitId),
          toUnitId: String(conversionData.toUnitId),
          conversionFactor: String(conversionData.conversionFactor),
          remarks: conversionData.remarks || '',
          isActive: conversionData.isActive !== false,
        });
      }
      setLoading(false);
    } catch (err: unknown) {
      setError(parseApiError(err, t('unitConversion.failedToLoadConversion')).message);
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'isActive') {
      setFormData((prev) => ({ ...prev, isActive: value === 'true' }));
      return;
    }

    let finalValue = value;
    if (name in MAX_LENGTHS && value.length > MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]) {
      finalValue = value.substring(0, MAX_LENGTHS[name as keyof typeof MAX_LENGTHS]);
    }

    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    // Clear field error when user starts typing
    setFieldErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    // From unit — required
    if (!formData.fromUnitId) {
      errors.fromUnitId = t('unitConversion.formValidation.fromUnitRequired');
      isValid = false;
    }

    // To unit — required + must differ from the from unit
    if (!formData.toUnitId) {
      errors.toUnitId = t('unitConversion.formValidation.toUnitRequired');
      isValid = false;
    } else if (formData.toUnitId === formData.fromUnitId) {
      errors.toUnitId = t('unitConversion.formValidation.unitsMustDiffer');
      isValid = false;
    }

    // Conversion factor — required positive number
    const factor = parseFloat(formData.conversionFactor);
    if (!formData.conversionFactor.trim() || isNaN(factor)) {
      errors.conversionFactor = t('unitConversion.formValidation.factorRequired');
      isValid = false;
    } else if (factor <= 0) {
      errors.conversionFactor = t('unitConversion.formValidation.factorPositive');
      isValid = false;
    }

    // Remarks — optional, max length only
    if (formData.remarks && formData.remarks.length > MAX_LENGTHS.remarks) {
      errors.remarks = t('unitConversion.formValidation.remarksMaxLength');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    if (!validateForm() || !conversion) {
      return;
    }

    setSaving(true);
    try {
      await unitConversionService.updateUnitConversion(conversion.unitConversionId, {
        fromUnitId: parseInt(formData.fromUnitId, 10),
        toUnitId: parseInt(formData.toUnitId, 10),
        conversionFactor: parseFloat(formData.conversionFactor),
        isProductSpecific: conversion.isProductSpecific,
        productId: conversion.productId,
        remarks: formData.remarks || undefined,
        isActive: formData.isActive,
      });
      navigate('/products/unit-conversions', {
        state: { message: t('unitConversion.updateSuccess') },
      });
    } catch (err: unknown) {
      setError(parseApiError(err, t('unitConversion.failedToUpdateConversion')).message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="edit-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!conversion) {
    return (
      <Layout>
        <div className="edit-container">
          <div className="alert alert-danger">{error || t('unitConversion.conversionNotFound')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="edit-container">
        {/* Navigation Breadcrumb */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/products/unit-conversions')}>
              <ArrowLeft size={16} strokeWidth={2} />
            </button>
            <div className="ims-page-title">
              {t('unitConversion.editConversion')}
              <span className="ims-page-subtitle">{conversion.fromUnitName} → {conversion.toUnitName}</span>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="ims-btn ims-btn-secondary" onClick={() => navigate('/products/unit-conversions')} disabled={saving}>
              {t('common.cancel')}
            </button>
            <button type="submit" form="page-form" className="ims-btn ims-btn-primary" disabled={saving}>
              {saving ? <><span className="ims-btn-spinner" />{t('common.saving')}</> : <><Save size={15} strokeWidth={2} />{t('common.saveChanges')}</>}
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            <strong>{t('common.error')}:</strong> {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="edit-content">
          {/* Form Section */}
          <div className="edit-form-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h2 className="edit-card-title">{t('unitConversion.editConversion')}</h2>
              </div>
              <div className="edit-card-body">
                <form id="page-form" onSubmit={handleSubmit}>
                  {/* From Unit */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unitConversion.fromUnit')} <span className="required">*</span>
                    </label>
                    <select
                      className={`form-input ${fieldErrors.fromUnitId ? 'is-invalid' : ''}`}
                      name="fromUnitId"
                      value={formData.fromUnitId}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('unitConversion.selectUnit')}</option>
                      {units.map((unit) => (
                        <option key={unit.unitId} value={unit.unitId}>
                          {unit.unitName} ({unit.unitShortName})
                        </option>
                      ))}
                    </select>
                    {fieldErrors.fromUnitId && <small className="form-error">{fieldErrors.fromUnitId}</small>}
                    {!fieldErrors.fromUnitId && <small className="form-helper">{t('unitConversion.fromUnitHint')}</small>}
                  </div>

                  {/* To Unit */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unitConversion.toUnit')} <span className="required">*</span>
                    </label>
                    <select
                      className={`form-input ${fieldErrors.toUnitId ? 'is-invalid' : ''}`}
                      name="toUnitId"
                      value={formData.toUnitId}
                      onChange={handleInputChange}
                    >
                      <option value="">{t('unitConversion.selectUnit')}</option>
                      {units.map((unit) => (
                        <option key={unit.unitId} value={unit.unitId}>
                          {unit.unitName} ({unit.unitShortName})
                        </option>
                      ))}
                    </select>
                    {fieldErrors.toUnitId && <small className="form-error">{fieldErrors.toUnitId}</small>}
                    {!fieldErrors.toUnitId && <small className="form-helper">{t('unitConversion.toUnitHint')}</small>}
                  </div>

                  {/* Conversion Factor */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unitConversion.conversionFactor')} <span className="required">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-input ${fieldErrors.conversionFactor ? 'is-invalid' : ''}`}
                      name="conversionFactor"
                      value={formData.conversionFactor}
                      onChange={handleInputChange}
                      placeholder="1.0"
                      step="any"
                      min="0"
                    />
                    {fieldErrors.conversionFactor && <small className="form-error">{fieldErrors.conversionFactor}</small>}
                    {!fieldErrors.conversionFactor && <small className="form-helper">{t('unitConversion.factorHint')}</small>}
                  </div>

                  {/* Remarks */}
                  <div className="form-group">
                    <label className="form-label">
                      {t('unitConversion.remarks')}
                      <span className="char-counter">{formData.remarks.length}/{MAX_LENGTHS.remarks}</span>
                    </label>
                    <textarea
                      className={`form-textarea ${fieldErrors.remarks ? 'is-invalid' : ''}`}
                      name="remarks"
                      value={formData.remarks}
                      onChange={handleInputChange}
                      placeholder={t('unitConversion.remarks')}
                      rows={4}
                      maxLength={MAX_LENGTHS.remarks}
                    />
                    {fieldErrors.remarks && <small className="form-error">{fieldErrors.remarks}</small>}
                    {!fieldErrors.remarks && <small className="form-helper">{t('unitConversion.remarksHint')}</small>}
                  </div>

                  {/* Status */}
                  <div className="form-group">
                    <label className="form-label">{t('common.status')}</label>
                    <select
                      className="form-input"
                      name="isActive"
                      value={formData.isActive ? 'true' : 'false'}
                      onChange={handleInputChange}
                    >
                      <option value="true">{t('common.active')}</option>
                      <option value="false">{t('common.inactive')}</option>
                    </select>
                  </div>

                </form>
              </div>
            </div>
          </div>

          {/* Info Sidebar */}
          <div className="edit-info-section">
            <div className="edit-card">
              <div className="edit-card-header">
                <h3 className="edit-card-title">{t('common.information')}</h3>
              </div>
              <div className="edit-card-body">
                <div className="info-item">
                  <label className="info-label">{t('common.status')}</label>
                  <div className="info-value">
                    <span className="info-badge info-badge-active">
                      {conversion.isActive ? t('common.active') : t('common.inactive')}
                    </span>
                  </div>
                </div>

                <div className="info-item">
                  <label className="info-label">{t('common.createdDate')}</label>
                  <div className="info-value">
                    {conversion.createdDate
                      ? new Date(conversion.createdDate).toLocaleDateString(i18n.language === 'ja' ? 'ja-JP' : 'en-US')
                      : '-'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default UnitConversionEditPage;
